import React, { useState, useEffect, useRef, useCallback } from "react";
import { withStyles, Button, CircularProgress } from "@material-ui/core";
import { Refresh } from "@material-ui/icons";
import { withSnackbar } from "notistack";
import { compose } from "recompose";
import { getDataWithQueryString } from "../../../../api/componentAPIs";
import SearchBox from "../../../common/SearchBox";
import DataGrid from '@dataGrid';
import styles from "../../Settings/styles";
import GridConfig from "./GridConfig";
import ClearAllFilterButton from '@dataGrid/components/columnFilters/ClearAllFilterButton';
import Select from "../../../../components/common/StyledSelect";
import { useAbortController } from "../../../common/hooks/useAbortController";

const MODULES = {
  SHIPMENTS: 'shipments',
  DISPATCH: 'dispatch'
}

const ShipmentsEventStream = ({ classes, enqueueSnackbar }) => {
  const { getNewAbortSignal, isMountedRef } = useAbortController();
  const [stagingEventsData, setStagingEventsData] = useState(null);
  const [id, setId] = useState(new Date().toString());
  const [selectedModule, setSelectedModule] = useState(MODULES.SHIPMENTS);
  const [isLoading, setIsLoading] = useState(false);
  const [quickFilterText, setQuickFilterText] = useState('');

  const gridApi = useRef(null);
  const gridConfig = useRef(new GridConfig());

  const onGridReady = ({ api }) => {
    gridApi.current = api;
  };

  const getTransformedData = (data) => {
    if (selectedModule === MODULES.SHIPMENTS) {
      return data
    }
    return data.map((item) => {
      return {
        id: item.eventId,
        type: item.eventType,
        status: item.status,
        createdDateTime: item.eventOccuredTime,
        data: item.eventData
      };
    });
  }

  const fetchShipmentEventStreamData = useCallback(() => {
    setId(new Date().toString());
    setStagingEventsData(null);
    setIsLoading(true);

    const api = selectedModule === MODULES.SHIPMENTS ? "GetStagingEvents" : "GetDispatchEvents";
    const signal = getNewAbortSignal(api);
    getDataWithQueryString(api, '', { signal })
      .then((response) => {
        if (isMountedRef.current) {
          // Only update data if the request wasn't cancelled
          if (response?.message !== "canceled" && response?.code !== "ERR_CANCELED") {
            setIsLoading(false);
            setStagingEventsData(getTransformedData(response.data ?? []));
          }
        }
      })
      .catch(() => {
        setIsLoading(false);
        setStagingEventsData([]);
        enqueueSnackbar("Unable to fetch data ", {
          variant: "error",
        });
      });
  }, [selectedModule, enqueueSnackbar]);

  useEffect(() => {
    fetchShipmentEventStreamData();
  }, [selectedModule, fetchShipmentEventStreamData]);

  const updateQuickFilterText = (value) => {
    setQuickFilterText(value);
  };

  const onCellClick = (params) => {
    if (params.column.colId === "copyIcon") {
      navigator.clipboard
        .writeText(JSON.stringify(params.data.data))
        .then((r) => {
          enqueueSnackbar("Copied data", { variant: "success" });
        });
    }
  };

  const handleModuleChange = (value) => {
    const selectedValue = value?.value;
    if (selectedModule !== selectedValue) {
      setSelectedModule(selectedValue);
    }
  };

  return (
    <div>
      <header className={classes.moduleHeader} style={{ borderBottom: 0 }}>
        <div className={classes.moduleHeaderContent}>
          <div className={classes.moduleHeaderContentLeft}>
            <div>Events Stream</div>
            <div>
              <Select
                data={[
                  { value: MODULES.SHIPMENTS, label: 'Shipments' },
                  { value: MODULES.DISPATCH, label: 'Dispatch' }
                ]}
                value={selectedModule}
                onChange={handleModuleChange}
                textFieldProps={{
                  label: 'Module',
                  variant: 'outlined'
                }}
                rootClass={classes.moduleSelect}
              />
            </div>
          </div>
          <div className={`${classes.gridActionIcons} ${classes.searchBoxContainer}`}>
            <Button
              color="primary"
              variant="contained"
              onClick={fetchShipmentEventStreamData}
              classes={{
                root: `${classes.actionButton} ${classes.unsetMinWidth}`,
                disabled: classes.actionDisabled
              }}
              disabled={isLoading}>
              {isLoading ? (
                <CircularProgress size={16} classes={{ root: classes.circularProgress }} />
              ) : (
                <Refresh fontSize="small" />
              )}
              Refresh
            </Button>
            <SearchBox
              onChange={updateQuickFilterText}
              customSearchClass={classes.customSearchWidth200}
            />
            <ClearAllFilterButton gridApi={gridApi.current} />
          </div>
        </div>
      </header>
      <div className={classes.reportsIBPlanningGridContainer}>
        <div className={`${classes.dataGrid} ${classes.anchorPointContent}`}>
          <DataGrid
            key={id}
            defaultColDef={gridConfig.current.config.defaultColDef}
            columnDefs={gridConfig.current.config.columns}
            config={gridConfig.current.config}
            rowData={stagingEventsData}
            quickFilterText={quickFilterText}
            onGridReady={onGridReady}
            rowHeight={120}
            onCellClicked={onCellClick}
          />
        </div>
      </div>
    </div>
  );
};

export default compose(withSnackbar, withStyles(styles))(ShipmentsEventStream);
